import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useParams } from "react-router-dom";
import {
  Home,
  HomeFill,
  Group,
  GroupFill,
  AutoAwesomeMosaic,
  AutoAwesomeMosaicFill,
  PieChart,
  PieChartFill,
  Assignment,
  AssignmentFill,
  Forum,
  ForumFill,
  MoreVert,
  MoreVertFill,
} from "@material-symbols-svg/react";
import Popover from "src/components/common/Popover";

type MaterialSymbolProps = React.ComponentProps<typeof Home>;

interface BottomBarProps {
  bottomBarItems: any;
  supportProgramKey: string;
}

const BottomBar: React.FC<BottomBarProps> = ({
  bottomBarItems,
  supportProgramKey,
}) => {
  const navigate = useNavigate();
  const params = useParams();
  const location = useLocation();
  const pathname = location.pathname;

  // Handle viewport resize for keyboard
  // useEffect(() => {
  //   console.log("ref", ref);
  //   const bottomMenu = ref.current;
  //   const viewport = window.visualViewport;

  //   if (bottomMenu && viewport) {
  //     const initialHeight = viewport.height;

  //     const handleViewportResize = () => {
  //       if (!viewport) return;

  //       // When keyboard opens, visual viewport height decreases.
  //       // We move the menu down by the difference to keep it at the bottom.
  //       const offset = initialHeight - viewport.height;

  //       // Only apply the transform if the keyboard is likely open
  //       if (offset > 100) {
  //         // 100px is a heuristic for keyboard height
  //         bottomMenu.style.transform = `translateY(${offset}px)`;
  //       } else {
  //         bottomMenu.style.transform = "translateY(0)";
  //       }
  //     };

  //     viewport.addEventListener("resize", handleViewportResize);

  //     return () => {
  //       viewport.removeEventListener("resize", handleViewportResize);
  //     };
  //   }
  // }, [ref]);

  /* Google Material Symbols (SVG) – BottomBar migrated from Lucide */
  const materialIcons: Record<
    string,
    React.ComponentType<MaterialSymbolProps>
  > = {
    "home-screen": Home,
    "program-screen": Group,
    "health-assistant-screen": AutoAwesomeMosaic,
    "insights-screen": PieChart,
    "records-screen": Assignment,
    "chat-screen": Forum,
    "more-screen": MoreVert,
  };

  /** Outline when inactive, filled when active (all tabs) */
  const getIconComponent = (
    screenKey: string,
    active: boolean
  ): React.ComponentType<MaterialSymbolProps> => {
    const outlineToFilled: Record<
      string,
      [
        React.ComponentType<MaterialSymbolProps>,
        React.ComponentType<MaterialSymbolProps>
      ]
    > = {
      "home-screen": [Home, HomeFill],
      "program-screen": [Group, GroupFill],
      "health-assistant-screen": [AutoAwesomeMosaic, AutoAwesomeMosaicFill],
      "insights-screen": [PieChart, PieChartFill],
      "records-screen": [Assignment, AssignmentFill],
      "chat-screen": [Forum, ForumFill],
      "more-screen": [MoreVert, MoreVertFill],
    };
    const pair = outlineToFilled[screenKey];
    return pair
      ? active
        ? pair[1]
        : pair[0]
      : materialIcons[screenKey] ?? Home;
  };

  const showMoreMenu = bottomBarItems?.length > 5;
  const menuItems = showMoreMenu ? bottomBarItems?.slice(0, 4) : bottomBarItems;

  if (showMoreMenu) {
    menuItems.push({
      screenName: "More",
      screenKey: "more-screen",
    });
  }

  const moreItems = showMoreMenu ? bottomBarItems?.slice(4) : [];
  const isMoreItemActive = moreItems.some(
    (moreItem: any) => moreItem.screenKey === params["screen-name"]
  );

  return (
    <div className="relative flex-shrink-0 bg-white border-t border-gray-200 px-2 py-2 rounded-t-2xl">
      <div className="flex justify-around">
        {menuItems?.map((item: any) => {
          const screenKeyValue = params["screen-name"];
          const isActive =
            item.screenKey === screenKeyValue ||
            pathname.includes(item.screenKey);

          const handleClick = (screenKey: string) => {
            navigate(`/app/${supportProgramKey}/${screenKey}`);
          };

          return item.screenName === "Health Assistant Screen" ? (
            isActive ? (
              <button
                className="flex flex-col items-center justify-center py-2 px-1 rounded-lg transition-all duration-300 min-w-0 flex-1 relative"
                onClick={() => handleClick(item.screenKey)}
              >
                <div
                  className="relative flex items-center justify-center w-10 h-10 rounded-full transition-all duration-300 scale-110"
                  style={{
                    background: `linear-gradient(135deg, var(--app-primary) 0%, var(--app-primary-hover) 100%)`,
                    boxShadow: `0 2px 12px rgba(var(--app-primary-rgb), 0.4)`,
                  }}
                >
                  <span
                    className="material-symbols-outlined w-6 h-6 transition-colors text-white drop-shadow-sm"
                    aria-hidden="true"
                    style={{
                      fontSize: 24,
                      lineHeight: 1,
                      width: 24,
                      height: 24,
                      fontVariationSettings:
                        '"FILL" 1, "wght" 400, "GRAD" 0, "opsz" 24',
                    }}
                  >
                    auto_awesome
                  </span>
                </div>
              </button>
            ) : (
              <button
                className="flex flex-col items-center justify-center py-2 px-1 rounded-lg transition-all duration-300 min-w-0 flex-1 relative"
                onClick={() => handleClick(item.screenKey)}
              >
                <div
                  className="relative flex items-center justify-center w-10 h-10 rounded-full transition-all duration-300 border-2"
                  style={{
                    background: `linear-gradient(135deg, rgba(var(--app-primary-rgb), 0.06), rgba(var(--app-primary-rgb), 0.02))`,
                    borderColor: `rgba(var(--app-primary-rgb), 0.35)`,
                  }}
                >
                  <span
                    className="material-symbols-outlined w-6 h-6 transition-colors text-[var(--app-primary)]"
                    aria-hidden="true"
                    style={{
                      fontSize: 24,
                      lineHeight: 1,
                      width: 24,
                      height: 24,
                      fontVariationSettings:
                        '"FILL" 0, "wght" 400, "GRAD" 0, "opsz" 24',
                    }}
                  >
                    auto_awesome
                  </span>
                </div>
              </button>
            )
          ) : item.screenName === "More" ? (
            <Popover
              label={
                <div className="flex flex-col items-center justify-center transition-all duration-300 min-w-0 flex-1 relative">
                  <MoreVert
                    size={20}
                    className={`w-5 h-5 transition-colors ${
                      isMoreItemActive
                        ? "text-[var(--app-primary)]"
                        : "text-gray-500"
                    }`}
                  />
                  <div
                    className={`text-xs font-medium text-center leading-tight transition-colors mt-1 ${
                      isMoreItemActive
                        ? "text-[var(--app-primary)]"
                        : "text-gray-500"
                    }`}
                  >
                    {item.screenName}
                  </div>
                </div>
              }
              position="top"
              className="flex flex-col items-center justify-center py-2 px-1 -mb-1 rounded-lg transition-all duration-300 min-w-0 flex-1 relative"
            >
              <ul className="w-full">
                {moreItems?.map((item: any) => {
                  const isMoreItemActive =
                    item.screenKey === params["screen-name"];
                  return (
                    <li key={item.screenName}>
                      <button
                        className={`flex items-center gap-2 text-sm w-full p-2 active:bg-gray-100 rounded-md ${
                          isMoreItemActive ? "text-secondary" : "text-gray-500"
                        }`}
                        onClick={() => handleClick(item.screenKey)}
                      >
                        {getIconComponent(item.screenKey, isMoreItemActive) && (
                          <div className="flex items-center justify-center">
                            {React.createElement(
                              getIconComponent(
                                item.screenKey,
                                isMoreItemActive
                              ),
                              {
                                size: 20,
                                className: `w-5 h-5 transition-colors ${
                                  isMoreItemActive
                                    ? "text-[var(--app-primary)]"
                                    : "text-gray-500"
                                }`,
                              }
                            )}
                          </div>
                        )}
                        <div
                          className={`text-sm font-medium text-center leading-tight transition-colors ${
                            isMoreItemActive
                              ? "text-[var(--app-primary)]"
                              : "text-gray-500"
                          }`}
                        >
                          {item.screenName}
                        </div>
                      </button>
                    </li>
                  );
                })}
              </ul>
            </Popover>
          ) : (
            <button
              key={item.screenName}
              className={`flex flex-col items-center justify-center flex-1 relative`}
              onClick={() => handleClick(item.screenKey)}
            >
              {getIconComponent(item.screenKey, isActive) && (
                <div
                  className={
                    isActive
                      ? "text-[var(--app-primary)] rounded-full px-4 py-2"
                      : "text-gray-500 rounded-full px-4 py-2 hover:bg-gray-100"
                  }
                  style={
                    isActive
                      ? {
                          background: "rgba(var(--app-primary-rgb), 0.08)",
                        }
                      : undefined
                  }
                >
                  {React.createElement(
                    getIconComponent(item.screenKey, isActive),
                    {
                      size: 24,
                      className: "w-6 h-6 transition-colors",
                    }
                  )}
                </div>
              )}
              {/* <div
                    className={`text-xs font-medium text-center leading-tight transition-colors mt-1 ${
                      isActive ? "text-[var(--app-primary)]" : "text-gray-500"
                    }`}
                  >
                    {item.screenName}
                  </div> */}
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default BottomBar;
